// Metadata tying an account-group scope to the endpoints its member picker uses.
//
// `codeType` and `searchKey` match AccountCodesController's `code_type_mapping`
// and per-segment `*_search` params; `codeType` doubles as the param name that
// pins already-selected ids into the paginated page.

export const WHOLE_ACCOUNT_SCOPE = 'account'

export const SEGMENT_SCOPES = [
  { scope: 'org', label: 'Org', codeType: 'orgs', searchKey: 'orgs_search' },
  { scope: 'object', label: 'Object', codeType: 'objects', searchKey: 'objects_search' },
  { scope: 'project', label: 'Project', codeType: 'projects', searchKey: 'projects_search' },
  { scope: 'seg_one', label: 'Seg One', codeType: 'segOnes', searchKey: 'seg_ones_search' },
  { scope: 'seg_two', label: 'Seg Two', codeType: 'segTwos', searchKey: 'seg_twos_search' },
  { scope: 'seg_three', label: 'Seg Three', codeType: 'segThrees', searchKey: 'seg_threes_search' },
  { scope: 'seg_four', label: 'Seg Four', codeType: 'segFours', searchKey: 'seg_fours_search' },
  { scope: 'seg_five', label: 'Seg Five', codeType: 'segFives', searchKey: 'seg_fives_search' },
  { scope: 'seg_six', label: 'Seg Six', codeType: 'segSixes', searchKey: 'seg_sixes_search' },
  { scope: 'seg_seven', label: 'Seg Seven', codeType: 'segSevens', searchKey: 'seg_sevens_search' },
  { scope: 'seg_eight', label: 'Seg Eight', codeType: 'segEights', searchKey: 'seg_eights_search' }
]

export const segmentScope = (accountType) =>
  SEGMENT_SCOPES.find((entry) => entry.scope === accountType)

// Municipalities rename their segments (seg_one may be "Fund"), and the rest of
// the app shows those names — the picker has to agree or the same dimension
// reads differently in two places.
//
// The fallback is the generic label, and it deliberately matches the wording the
// backend (MunicipalitySetting::SEGMENT_DEFAULTS) and the duties account screens
// use: "Seg Eight", not "Segment Eight". An unconfigured segment showing two
// different names on two screens reads as two different dimensions.
export const segmentLabel = (entry, settings) =>
  settings?.[`${entry.scope}_name`]?.trim() || entry.label

// A group as one string: its code and name rejoined, the way it reads on a chart
// axis or in an export (AccountGroup#display_label). The API sends this as
// `label`; composing it here as a fallback keeps a group nameable when a cached
// payload predates that field.
export const groupLabel = (group) =>
  group?.label || [group?.code, group?.name].filter(Boolean).join(' ')

// The label shown for a group's dimension in the list and in the column it feeds.
export const scopeLabel = (group, settings) => {
  if (group?.group_type === WHOLE_ACCOUNT_SCOPE) return 'Account'

  const entry = segmentScope(group?.account_type)
  if (!entry) return group?.account_type || ''

  return segmentLabel(entry, settings)
}

// Every account segment the filter offers, in account-string order.
//
// The full segment list rather than only the segments that happen to have groups:
// the filter doubles as the answer to "which segments could I group?", and Org and
// Project are as real as the numbered ones even before anyone groups them. Labels
// come from the municipality's own segment names, so this list renames with them.
//
// Ordered by position in the account string, not alphabetically. Sorting by label
// interleaves the segments meaninglessly ("Seg Eight, Seg Five, Seg Four, Seg One")
// and reorders itself as soon as a municipality renames a segment.
//
// Segments only — the whole-account scope is deliberately absent. It isn't a segment
// of the account string, so it has no place in a list of them; whole-account groups
// still show under "All account segments".
export const groupDimensions = (settings) =>
  SEGMENT_SCOPES.map((entry) => ({ key: entry.scope, label: segmentLabel(entry, settings) }))
